import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import SafetyScoreWidget from "@/components/SafetyScoreWidget";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Users, 
  BookOpen, 
  CheckCircle2,
  Clock,
  AlertTriangle,
  Plus,
  ArrowRight,
  Upload,
  FileSpreadsheet,
  Download
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showAssignCourse, setShowAssignCourse] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [newEmployee, setNewEmployee] = useState({ name: '', email: '', department: '', job_title: '' });
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, employeesRes, assignmentsRes, coursesRes] = await Promise.all([
        axios.get(`${API}/stats/company`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true }),
        axios.get(`${API}/assignments`, { withCredentials: true }),
        axios.get(`${API}/courses`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setEmployees(employeesRes.data);
      setAssignments(assignmentsRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employees`, newEmployee, { withCredentials: true });
      toast.success("Employee added successfully");
      setShowAddEmployee(false);
      setNewEmployee({ name: '', email: '', department: '', job_title: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add employee");
    }
  };

  const handleAssignCourse = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/assignments`, {
        employee_id: selectedEmployee,
        course_id: selectedCourse
      }, { withCredentials: true });
      toast.success("Course assigned successfully");
      setShowAssignCourse(false);
      setSelectedEmployee('');
      setSelectedCourse('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign course");
    }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }
    
    setImporting(true);
    setImportResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/employees/bulk-import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResults(response.data);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
      setImportResults({ error: error.response?.data?.detail || "Import failed" });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadCsvTemplate = () => {
    const csvContent = "name,email,department,job_title\nJohn Doe,john@example.com,Operations,Safety Manager\nJane Smith,jane@example.com,Warehouse,Forklift Operator";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const statCards = [
    { 
      title: "Total Employees", 
      value: stats?.employee_count || 0, 
      icon: Users, 
      color: "text-blue-600",
      bgColor: "bg-blue-100"
    },
    { 
      title: "Completed Training", 
      value: stats?.completed_assignments || 0, 
      icon: CheckCircle2, 
      color: "text-emerald-600",
      bgColor: "bg-emerald-100"
    },
    { 
      title: "In Progress", 
      value: stats?.in_progress_assignments || 0, 
      icon: Clock, 
      color: "text-amber-600",
      bgColor: "bg-amber-100"
    },
    { 
      title: "Overdue", 
      value: stats?.overdue_assignments || 0, 
      icon: AlertTriangle, 
      color: "text-rose-600",
      bgColor: "bg-rose-100"
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-8" data-testid="company-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
              {stats?.company?.name || 'Company Dashboard'}
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your team's safety training and compliance.
            </p>
          </div>
          <div className="flex gap-3">
            <Dialog open={showAddEmployee} onOpenChange={setShowAddEmployee}>
              <DialogTrigger asChild>
                <Button data-testid="add-employee-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Employee
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Employee</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddEmployee} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={newEmployee.name}
                      onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                      placeholder="John Doe"
                      required
                      data-testid="employee-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newEmployee.email}
                      onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                      placeholder="john@company.com"
                      required
                      data-testid="employee-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={newEmployee.department}
                      onChange={(e) => setNewEmployee({...newEmployee, department: e.target.value})}
                      placeholder="Operations"
                      data-testid="employee-department-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="job_title">Job Title</Label>
                    <Input
                      id="job_title"
                      value={newEmployee.job_title}
                      onChange={(e) => setNewEmployee({...newEmployee, job_title: e.target.value})}
                      placeholder="Safety Manager"
                      data-testid="employee-title-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-employee-btn">
                    Add Employee
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={showAssignCourse} onOpenChange={setShowAssignCourse}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="assign-course-btn">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Assign Course
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Assign Training Course</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAssignCourse} className="space-y-4">
                  <div>
                    <Label htmlFor="employee">Select Employee</Label>
                    <select
                      id="employee"
                      value={selectedEmployee}
                      onChange={(e) => setSelectedEmployee(e.target.value)}
                      className="w-full h-10 px-3 border border-slate-200 rounded-md"
                      required
                      data-testid="select-employee"
                    >
                      <option value="">Choose an employee...</option>
                      {employees.map((emp) => (
                        <option key={emp.employee_id} value={emp.employee_id}>
                          {emp.name} ({emp.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="course">Select Course</Label>
                    <select
                      id="course"
                      value={selectedCourse}
                      onChange={(e) => setSelectedCourse(e.target.value)}
                      className="w-full h-10 px-3 border border-slate-200 rounded-md"
                      required
                      data-testid="select-course"
                    >
                      <option value="">Choose a course...</option>
                      {courses.map((course) => (
                        <option key={course.course_id} value={course.course_id}>
                          {course.title} ({course.duration_minutes} min)
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-assignment-btn">
                    Assign Course
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Bulk Import Dialog */}
            <Dialog open={showBulkImport} onOpenChange={(open) => {
              setShowBulkImport(open);
              if (!open) setImportResults(null);
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="bulk-import-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Bulk Import Employees</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Upload a CSV file with columns: <strong>name</strong>, <strong>email</strong> (required), 
                    <strong>department</strong>, <strong>job_title</strong> (optional)
                  </p>
                  
                  <Button 
                    variant="outline" 
                    onClick={downloadCsvTemplate}
                    className="w-full"
                    data-testid="download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                  
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleBulkImport}
                      className="hidden"
                      id="csv-upload"
                      data-testid="csv-file-input"
                    />
                    <label htmlFor="csv-upload">
                      <Button 
                        variant="secondary" 
                        disabled={importing}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="select-file-btn"
                      >
                        {importing ? "Importing..." : "Select CSV File"}
                      </Button>
                    </label>
                  </div>
                  
                  {importResults && (
                    <div className="bg-secondary rounded-lg p-4 text-sm">
                      {importResults.error ? (
                        <p className="text-destructive">{importResults.error}</p>
                      ) : (
                        <div className="space-y-2">
                          <p className="font-semibold text-emerald-600">{importResults.message}</p>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-emerald-100 rounded p-2">
                              <p className="font-bold text-emerald-700">{importResults.created}</p>
                              <p className="text-xs text-emerald-600">Created</p>
                            </div>
                            <div className="bg-amber-100 rounded p-2">
                              <p className="font-bold text-amber-700">{importResults.skipped}</p>
                              <p className="text-xs text-amber-600">Skipped</p>
                            </div>
                            <div className="bg-rose-100 rounded p-2">
                              <p className="font-bold text-rose-700">{importResults.errors}</p>
                              <p className="text-xs text-rose-600">Errors</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <Card key={i} className="stat-card" data-testid={`company-stat-${i}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                      <p className="font-heading text-2xl font-bold text-primary">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Safety Score Widget */}
        <SafetyScoreWidget />

        {/* Compliance Rate */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading font-semibold text-primary">Overall Compliance Rate</h3>
                <p className="text-sm text-muted-foreground">Training completion across all employees</p>
              </div>
              <span className="font-heading text-3xl font-bold text-primary">{stats?.completion_rate || 0}%</span>
            </div>
            <Progress value={stats?.completion_rate || 0} className="h-3" />
          </CardContent>
        </Card>

        {/* Employees & Assignments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Employees List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Team Members</CardTitle>
              <Badge variant="secondary">{employees.length} total</Badge>
            </CardHeader>
            <CardContent>
              {employees.length > 0 ? (
                <div className="space-y-3">
                  {employees.slice(0, 5).map((emp) => (
                    <div key={emp.employee_id} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-semibold">
                          {emp.name?.charAt(0) || 'E'}
                        </div>
                        <div>
                          <p className="font-medium text-primary">{emp.name}</p>
                          <p className="text-sm text-muted-foreground">{emp.department || 'No department'}</p>
                        </div>
                      </div>
                      <Badge className={`badge-${emp.status === 'active' ? 'success' : 'neutral'}`}>
                        {emp.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No employees yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Assignments */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Training Assignments</CardTitle>
              <Badge variant="secondary">{assignments.length} total</Badge>
            </CardHeader>
            <CardContent>
              {assignments.length > 0 ? (
                <div className="space-y-3">
                  {assignments.slice(0, 5).map((assignment) => {
                    const employee = employees.find(e => e.employee_id === assignment.employee_id);
                    const course = courses.find(c => c.course_id === assignment.course_id);
                    return (
                      <div key={assignment.assignment_id} className="p-3 bg-secondary rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-primary">{course?.title || 'Course'}</p>
                          <Badge className={`badge-${
                            assignment.status === 'completed' ? 'success' : 
                            assignment.status === 'overdue' ? 'danger' : 
                            assignment.status === 'in_progress' ? 'warning' : 'neutral'
                          }`}>
                            {assignment.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{employee?.name || 'Employee'}</p>
                        <Progress value={assignment.progress} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No assignments yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CompanyDashboard;
